import {
  ArrowBackOutlined,
  ArrowForwardOutlined,
  AttachFileOutlined,
  RestartAltOutlined,
  SaveOutlined,
  SendOutlined,
} from '@mui/icons-material'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useAppController } from 'customHooks/useAppController'
import { WorkingStepsFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/WorkingStepsFilter/WorkingStepsFilter'
import { projectOrderNumberSelector } from 'features/BillOfMaterials/store/selectors/projectOrderNumberSelector'
import { projectReferenceSelector } from 'features/BillOfMaterials/store/selectors/projectReferenceSelector'
import { useBomItemThumbnailImageSrc } from 'features/BillOfMaterials/useBomItemThumbnailImageSrc'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { ProjectSuppliersController } from 'features/ProjectSuppliers/ProjectSuppliersController'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { BomItemType } from 'model/Project/BoMItemRow'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import {
  ProjectSupplierDto,
  QuotationExchangeChannel,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import store, { useAppSelector } from 'store/configureStore'
import { useShowException } from 'utils/useShowException'
import { ProjectRFQLines, RFQLine } from '../Components/ProjectRFQLines'
import {
  activityKey,
  preferredChannel,
  ProjectRFQController,
  SupplierAllocation,
} from '../ProjectRFQController'
import { parseQuantityBreaks } from './quantityBreaks'

export type DraftRFQDialogProps = { isOpen: boolean; onClose: () => void }
const steps = [
  'Scope',
  'Request details',
  'Quantity breaks',
  'Suppliers',
  'Review',
]

const supplierLabel = (supplier: ProjectSupplierDto) =>
  supplier.name || supplier.glNumber || 'Unnamed supplier'

const QuantityBreakItemThumbnail = ({
  pointer,
  itemName,
  rowNumber,
}: {
  pointer: BomItemPointer
  itemName: string
  rowNumber?: number
}) => {
  const { imageSrc } = useBomItemThumbnailImageSrc(pointer)

  return imageSrc ? (
    <Box
      component="img"
      src={imageSrc}
      alt={`${itemName} thumbnail`}
      sx={{
        width: 42,
        height: 42,
        flex: '0 0 auto',
        objectFit: 'contain',
        borderRadius: 1,
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'divider',
      }}
    />
  ) : (
    <Box
      sx={{
        display: 'grid',
        placeItems: 'center',
        flex: '0 0 auto',
        width: 42,
        height: 42,
        borderRadius: 1,
        bgcolor: 'action.selected',
        color: 'text.secondary',
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {rowNumber ?? '-'}
    </Box>
  )
}

export const DraftRFQDialog = ({ isOpen, onClose }: DraftRFQDialogProps) => {
  const { controller, loading } = useAppController(
    () => new ProjectRFQController(),
  )
  const { controller: suppliersController } = useAppController(
    () => new ProjectSuppliersController(),
  )
  const showException = useShowException('request for quotation')
  const [activeStep, setActiveStep] = useState(0)
  const [lines, setLines] = useState<RFQLine[]>([])
  const [selectedWorkingSteps, setSelectedWorkingSteps] = useState<
    WorkingStepType[]
  >([])
  const [selectedBomItemIds, setSelectedBomItemIds] = useState<Set<string>>(
    new Set(),
  )
  const [title, setTitle] = useState('')
  const [titleEdited, setTitleEdited] = useState(false)
  const orderNumber = useAppSelector(projectOrderNumberSelector)
  const projectReference = useAppSelector(projectReferenceSelector)
  const [buyerReference, setBuyerReference] = useState(projectReference ?? '')
  const [responseDeadlineWorkingDays, setResponseDeadlineWorkingDays] =
    useState('5')
  const [requestedDeliveryWorkingDays, setRequestedDeliveryWorkingDays] =
    useState('')
  const [buyerMessage, setBuyerMessage] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const [bulkTiers, setBulkTiers] = useState('')
  const [bulkTierError, setBulkTierError] = useState('')
  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, string>>(
    {},
  )
  const [quantityErrors, setQuantityErrors] = useState<Record<string, string>>(
    {},
  )
  const [suppliers, setSuppliers] = useState<ProjectSupplierDto[]>([])
  const [allocations, setAllocations] = useState<Record<string, string[]>>({})
  const [channels, setChannels] = useState<
    Record<string, QuotationExchangeChannel>
  >({})
  const [supplierLoading, setSupplierLoading] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [stepError, setStepError] = useState('')

  const selectedActivities = useMemo(
    () =>
      lines.flatMap((line) =>
        (line.activities ?? [])
          .filter((activity) => activity.selected)
          .map((activity) => ({
            line,
            activity,
            key: activityKey(line.bomItemPointer?.id, activity.activityId),
          })),
      ),
    [lines],
  )

  const generatedTitle = useMemo(() => {
    const workingSteps = [
      ...new Set(
        selectedActivities.flatMap(({ activity }) =>
          activity.workingStepType ? [activity.workingStepType] : [],
        ),
      ),
    ]
    if (!workingSteps.length) return ''
    return `${orderNumber || 'No order number'} - ${projectReference || 'No project reference'} - (${workingSteps.join(' - ')})`
  }, [orderNumber, projectReference, selectedActivities])

  useEffect(() => {
    if (!titleEdited) setTitle(generatedTitle)
  }, [generatedTitle, titleEdited])

  useEffect(() => {
    if (!isOpen || activeStep < 3 || suppliers.length > 0) return
    setSupplierLoading(true)
    suppliersController
      .getSuppliers()
      .then((response) => {
        const available = response.projectSuppliers ?? []
        setSuppliers(available)
        setChannels(
          Object.fromEntries(
            available.flatMap((supplier) =>
              supplier.supplierId
                ? [[supplier.supplierId, preferredChannel(supplier)]]
                : [],
            ),
          ),
        )
      })
      .catch(showException)
      .finally(() => setSupplierLoading(false))
  }, [
    activeStep,
    controller,
    isOpen,
    showException,
    suppliers.length,
    suppliersController,
  ])

  const applySelectedWorkingSteps = useCallback(() => {
    const project = store.getState().project
    if (!project) return
    const selected = new Set(selectedWorkingSteps)
    const existingActivities = new Map(
      lines.flatMap((line) =>
        (line.activities ?? []).map((activity) => [
          activityKey(line.bomItemPointer?.id, activity.activityId),
          activity,
        ]),
      ),
    )
    const next: RFQLine[] = []
    for (const assemblyId of project.filteredAssemblyHeaderIds) {
      const assembly = project.assemblyHeaders[assemblyId]
      const assemblyQuantity =
        assembly.financial?.totalProjectQuantity ?? assembly.financial?.quantity
      const assemblyActivities = assembly.assemblyActivities.filter(
        (activity) => selected.has(activity.primaryWorkingStep),
      )
      if (assemblyActivities.length) {
        next.push({
          bomItemPointer: { id: assemblyId, type: BomItemType.assemblyType },
          activities: assemblyActivities.map((activity) => {
            const existing = existingActivities.get(
              activityKey(assemblyId, activity.id),
            )
            return {
              activityId: activity.id,
              workingStepType: activity.primaryWorkingStep,
              selected: existing?.selected ?? true,
              quantityBreaks:
                existing?.quantityBreaks ??
                (assemblyQuantity && assemblyQuantity > 0
                  ? [assemblyQuantity]
                  : undefined),
            }
          }),
        })
      }
      for (const pointer of assembly.filteredPartTypePointers) {
        const part = project.partTypes[pointer.id]
        const partQuantity =
          part.financial?.totalProjectQuantity ?? part.financial?.quantity
        const activities = part.activities.filter((activity) =>
          selected.has(activity.primaryWorkingStep),
        )
        if (!activities.length) continue
        next.push({
          bomItemPointer: { id: pointer.id, type: BomItemType.partType },
          activities: activities.map((activity) => {
            const existing = existingActivities.get(
              activityKey(pointer.id, activity.id),
            )
            return {
              activityId: activity.id,
              workingStepType: activity.primaryWorkingStep,
              selected: existing?.selected ?? true,
              quantityBreaks:
                existing?.quantityBreaks ??
                (partQuantity && partQuantity > 0
                  ? [partQuantity]
                  : undefined),
            }
          }),
        })
      }
    }
    setLines(next)
    setStepError(
      next.length
        ? ''
        : 'No activities match the selected working steps in the visible project scope.',
    )
  }, [lines, selectedWorkingSteps])

  const setActivitySelected = (
    bomItemId: string,
    index: number,
    selected: boolean,
  ) =>
    setLines((current) =>
      current.map((line) =>
        line.bomItemPointer?.id === bomItemId
          ? {
              ...line,
              activities: line.activities?.map((activity, i) =>
                i === index ? { ...activity, selected } : activity,
              ),
            }
          : line,
      ),
    )

  const setActivityTiers = (key: string, value: string) => {
    const parsed = parseQuantityBreaks(value)
    setQuantityErrors((current) => ({ ...current, [key]: parsed.error }))
    if (parsed.error) return
    setLines((current) =>
      current.map((line) => ({
        ...line,
        activities: line.activities?.map((activity) =>
          activityKey(line.bomItemPointer?.id, activity.activityId) === key
            ? { ...activity, quantityBreaks: parsed.quantities }
            : activity,
        ),
      })),
    )
    setQuantityDrafts((current) => ({
      ...current,
      [key]: parsed.quantities.join(', '),
    }))
  }

  const validateStep = (step: number) => {
    if (step === 0 && selectedActivities.length === 0)
      return 'Select at least one activity to quote.'
    if (step === 1 && !title.trim())
      return 'Enter a title so this request is recognizable.'
    const responseDays = Number(responseDeadlineWorkingDays)
    const deliveryDays = Number(requestedDeliveryWorkingDays)
    if (
      step === 1 &&
      (!Number.isInteger(responseDays) || responseDays <= 0)
    )
      return 'Enter a positive whole number of working days for responses.'
    if (
      step === 1 &&
      requestedDeliveryWorkingDays &&
      (!Number.isInteger(deliveryDays) || deliveryDays <= 0)
    )
      return 'Enter a positive whole number of working days for delivery.'
    if (
      step === 1 &&
      requestedDeliveryWorkingDays &&
      deliveryDays < responseDays
    )
      return 'Requested delivery cannot be sooner than the response deadline.'
    if (
      step === 2 &&
      selectedActivities.some(
        ({ activity }) => !activity.quantityBreaks?.length,
      )
    )
      return 'Add at least one positive quantity break to every selected activity.'
    if (
      step === 3 &&
      selectedActivities.some(
        ({ key }) =>
          !Object.values(allocations).some((keys) => keys.includes(key)),
      )
    )
      return 'Assign every selected activity to at least one supplier.'
    return ''
  }

  const next = () => {
    const error = validateStep(activeStep)
    setStepError(error)
    if (!error) setActiveStep((step) => Math.min(step + 1, steps.length - 1))
  }

  const goToStep = (targetStep: number) => {
    if (targetStep <= activeStep) {
      setStepError('')
      setActiveStep(targetStep)
      return
    }

    for (let step = activeStep; step < targetStep; step += 1) {
      const error = validateStep(step)
      if (error) {
        setActiveStep(step)
        setStepError(error)
        return
      }
    }

    setStepError('')
    setActiveStep(targetStep)
  }

  const save = async (publish: boolean) => {
    for (let step = 0; step <= (publish ? 3 : 2); step += 1) {
      const error = validateStep(step)
      if (error) {
        setActiveStep(step)
        setStepError(error)
        return
      }
    }
    setPublishing(publish)
    try {
      const response = await controller.createRequestForQuotation(lines, {
        title: title.trim(),
        buyerReference: buyerReference.trim() || undefined,
        responseDeadlineWorkingDays: Number(responseDeadlineWorkingDays),
        requestedDeliveryWorkingDays: requestedDeliveryWorkingDays
          ? Number(requestedDeliveryWorkingDays)
          : undefined,
        buyerMessage: buyerMessage.trim() || undefined,
        attachments: [],
      })
      if (!response.success)
        throw new Error(
          response.errorMessage || 'The draft could not be saved.',
        )
      if (attachments.length) {
        await controller.uploadAttachments(response.rfqId, attachments)
      }
      if (publish) {
        const supplierAllocations: SupplierAllocation[] = suppliers.flatMap(
          (supplier) => {
            const supplierId = supplier.supplierId
            if (!supplierId || !allocations[supplierId]?.length) return []
            return [
              {
                supplier,
                activityKeys: allocations[supplierId],
                channel:
                  channels[supplierId] ?? QuotationExchangeChannel.Manual,
              },
            ]
          },
        )
        const outcomes = await controller.publishRequestForQuotation(
          response.rfqId,
          supplierAllocations,
        )
        const failed = outcomes.filter(
          (outcome) => outcome.result.status === 'rejected',
        )
        if (failed.length)
          throw new Error(
            `${failed.length} supplier invitation${failed.length === 1 ? '' : 's'} failed. The draft was saved; open Manage RFQs to retry.`,
          )
      }
      onClose()
    } catch (error) {
      showException(error)
    } finally {
      setPublishing(false)
    }
  }

  const renderScope = () => (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      <Box
        sx={{
          minWidth: { md: 230 },
          borderRight: { md: 1 },
          borderColor: 'divider',
          pr: { md: 2 },
        }}
      >
        <Typography variant="overline">Filter by working steps</Typography>
        <WorkingStepsFilter
          selectedFilters={selectedWorkingSteps}
          onFilterChange={(step) =>
            step &&
            setSelectedWorkingSteps((current) =>
              current.includes(step)
                ? current.filter((item) => item !== step)
                : [...current, step],
            )
          }
        />
        <Button
          fullWidth
          variant="contained"
          size="small"
          startIcon={<PlaylistAddIcon />}
          onClick={applySelectedWorkingSteps}
          disabled={!selectedWorkingSteps.length}
          sx={{ mt: 1 }}
        >
          Apply & load items
        </Button>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {!lines.length ? (
          <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
            Choose working steps to discover quoteable activities.
          </Typography>
        ) : (
          <ProjectRFQLines
            rfqLines={lines}
            onSelectionChange={(id, checked) =>
              setSelectedBomItemIds((current) => {
                const next = new Set(current)
                if (checked) next.add(id)
                else next.delete(id)
                return next
              })
            }
            selectedBomItemIds={selectedBomItemIds}
            onLineActivitySelectionChange={setActivitySelected}
            showSupplierAssignment={false}
            summaryActions={
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  onClick={() =>
                    setLines((current) =>
                      current.map((line) => ({
                        ...line,
                        activities: line.activities?.map((activity) => ({
                          ...activity,
                          selected: true,
                        })),
                      })),
                    )
                  }
                >
                  Select all visible activities
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() =>
                    setLines((current) =>
                      current.map((line) => ({
                        ...line,
                        activities: line.activities?.map((activity) => ({
                          ...activity,
                          selected: false,
                        })),
                      })),
                    )
                  }
                >
                  Clear selection
                </Button>
              </Stack>
            }
          />
        )}
      </Box>
    </Box>
  )

  const renderDetails = () => (
    <Stack spacing={2} sx={{ maxWidth: 760, mx: 'auto' }}>
      <Typography variant="h6">
        Make the request easy to recognize and answer
      </Typography>
      <TextField
        required
        label="RFQ title"
        value={title}
         onChange={(event) => {
           setTitle(event.target.value)
           setTitleEdited(true)
         }}
        autoFocus
      />
      <TextField
        label="Buyer reference"
        value={buyerReference}
        onChange={(event) => setBuyerReference(event.target.value)}
        helperText="Optional internal or supplier-facing reference"
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          required
          fullWidth
          type="number"
          label="Response deadline (working days)"
          value={responseDeadlineWorkingDays}
          onChange={(event) =>
            setResponseDeadlineWorkingDays(event.target.value)
          }
          slotProps={{ htmlInput: { min: 1, step: 1 } }}
          helperText="Working days from the date the RFQ is sent"
        />
        <TextField
          fullWidth
          type="number"
          label="Requested delivery (working days)"
          value={requestedDeliveryWorkingDays}
          onChange={(event) =>
            setRequestedDeliveryWorkingDays(event.target.value)
          }
          slotProps={{ htmlInput: { min: 1, step: 1 } }}
          helperText="Optional; counted from the date the RFQ is sent"
        />
      </Stack>
      <TextField
        multiline
        minRows={4}
        label="Message to suppliers"
        value={buyerMessage}
        onChange={(event) => setBuyerMessage(event.target.value)}
      />
      <Button
        component="label"
        variant="outlined"
        startIcon={<AttachFileOutlined />}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add attachments
        <input
          hidden
          multiple
          type="file"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setAttachments(Array.from(event.target.files ?? []))
          }
        />
      </Button>
      {!!attachments.length && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {attachments.map((file) => (
            <Chip
              key={`${file.name}-${file.size}`}
              label={file.name}
              onDelete={() =>
                setAttachments((current) =>
                  current.filter((item) => item !== file),
                )
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  )

  const renderQuantities = () => {
    const project = store.getState().project

    const applyBulkTiers = () => {
      const parsed = parseQuantityBreaks(bulkTiers)
      setBulkTierError(parsed.error)
      if (parsed.error) return

      const value = parsed.quantities.join(', ')
      setLines((current) =>
        current.map((line) => ({
          ...line,
          activities: line.activities?.map((activity) =>
            activity.selected
              ? { ...activity, quantityBreaks: parsed.quantities }
              : activity,
          ),
        })),
      )
      setQuantityDrafts((current) => ({
        ...current,
        ...Object.fromEntries(
          selectedActivities.map(({ key }) => [key, value]),
        ),
      }))
      setQuantityErrors((current) => ({
        ...current,
        ...Object.fromEntries(selectedActivities.map(({ key }) => [key, ''])),
      }))
    }

    return (
      <Stack spacing={1.5} sx={{ height: '100%', minHeight: 0 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Set requested quantities
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Every activity starts at its item quantity for the full project. Add
            price-break quantities where you want suppliers to quote
            alternatives.
          </Typography>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            bgcolor: 'action.hover',
            borderColor: 'primary.main',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            sx={{ alignItems: { md: 'flex-start' } }}
          >
            <Box sx={{ minWidth: { md: 180 }, pt: { md: 0.5 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Apply to all {selectedActivities.length} activities
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Replaces every row below
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              label="Quantity breaks"
              placeholder="10, 50, 100"
              value={bulkTiers}
              error={Boolean(bulkTierError)}
              helperText={
                bulkTierError ||
                'Separate quantities with commas, spaces, or semicolons'
              }
              onChange={(event) => {
                setBulkTiers(event.target.value)
                if (bulkTierError) setBulkTierError('')
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') applyBulkTiers()
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">pcs</InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="contained"
              onClick={applyBulkTiers}
              sx={{ minWidth: 120, height: 40, whiteSpace: 'nowrap' }}
            >
              Apply to all
            </Button>
          </Stack>
        </Paper>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ flex: 1, minHeight: 240 }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '38%' }}>Item</TableCell>
                <TableCell sx={{ width: '22%' }}>Activity</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>
                  Project qty.
                </TableCell>
                <TableCell sx={{ minWidth: 270 }}>
                  Requested quantities
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedActivities.map(({ line, activity, key }) => {
                const pointer = line.bomItemPointer
                const item =
                  pointer?.type === BomItemType.assemblyType
                    ? project.assemblyHeaders?.[pointer.id]
                    : pointer?.type === BomItemType.partType
                      ? project.partTypes?.[pointer.id]
                      : undefined
                const itemName =
                  item?.name || item?.originalFileName || 'Unnamed BOM item'
                const itemQuantity =
                  item?.financial?.totalProjectQuantity ??
                  item?.financial?.quantity
                const draft =
                  quantityDrafts[key] ??
                  activity.quantityBreaks?.join(', ') ??
                  ''

                return (
                  <TableRow key={key} hover>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={1.25}
                        sx={{ alignItems: 'center' }}
                      >
                        {pointer && (
                          <QuantityBreakItemThumbnail
                            pointer={pointer}
                            itemName={itemName}
                            rowNumber={item?.rowNumber}
                          />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                            noWrap
                          >
                            {itemName}
                          </Typography>
                          <Tooltip
                            title={pointer?.id ?? ''}
                            placement="bottom-start"
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', maxWidth: 300 }}
                              noWrap
                            >
                              {`${item?.rowNumber ? `#${item.rowNumber} · ` : ''}${
                                item?.drawingNumber
                                  ? `Drawing ${item.drawingNumber}${item.revision ? ` · Rev. ${item.revision}` : ''}`
                                  : pointer?.type === BomItemType.assemblyType
                                    ? 'Assembly'
                                    : 'Part'
                              }`}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={activity.workingStepType || 'Unknown activity'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {itemQuantity ?? '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ alignItems: 'flex-start' }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          value={draft}
                          error={Boolean(quantityErrors[key])}
                          helperText={quantityErrors[key] || undefined}
                          onChange={(event) => {
                            const value = event.target.value
                            setQuantityDrafts((current) => ({
                              ...current,
                              [key]: value,
                            }))
                            if (quantityErrors[key])
                              setQuantityErrors((current) => ({
                                ...current,
                                [key]: '',
                              }))
                          }}
                          onBlur={(event) =>
                            setActivityTiers(key, event.target.value)
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter')
                              event.currentTarget.blur()
                          }}
                          slotProps={{
                            htmlInput: {
                              'aria-label': `Requested quantities for ${itemName} ${activity.workingStepType ?? ''}`,
                            },
                          }}
                        />
                        <Tooltip title="Reset to project quantity">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!itemQuantity}
                              aria-label={`Reset ${itemName} to project quantity`}
                              onClick={() => {
                                if (!itemQuantity) return
                                const value = String(itemQuantity)
                                setQuantityDrafts((current) => ({
                                  ...current,
                                  [key]: value,
                                }))
                                setActivityTiers(key, value)
                              }}
                            >
                              <RestartAltOutlined fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    )
  }

  const renderSuppliers = () => {
    if (supplierLoading)
      return (
        <Box sx={{ display: 'grid', placeItems: 'center', height: 240 }}>
          <CircularProgress aria-label="Loading suppliers" />
        </Box>
      )

    if (!suppliers.length)
      return (
        <Alert severity="warning">
          No eligible project suppliers are available. Add supplier capabilities
          and a quotation contact before publishing.
        </Alert>
      )

    const activitiesByType = new Map<
      WorkingStepType | undefined,
      typeof selectedActivities
    >()
    for (const selectedActivity of selectedActivities) {
      const type = selectedActivity.activity.workingStepType
      activitiesByType.set(type, [
        ...(activitiesByType.get(type) ?? []),
        selectedActivity,
      ])
    }

    return (
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Choose suppliers for each activity
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your BOM items and activities are already selected. Each choice
            below applies to every selected item with that activity.
          </Typography>
        </Box>

        {[...activitiesByType.entries()].map(([activityType, activities]) => {
          const activityKeys = activities.map(({ key }) => key)
          const compatibleSuppliers = suppliers.filter(
            (supplier) =>
              supplier.supplierId &&
              supplier.activities?.some(
                (capability) => capability.workingStepType === activityType,
              ),
          )
          const selectedSuppliers = compatibleSuppliers.filter((supplier) =>
            activityKeys.every((key) =>
              allocations[supplier.supplierId!]?.includes(key),
            ),
          )

          return (
            <Paper
              key={activityType ?? 'unknown'}
              variant="outlined"
              sx={{ p: 2 }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ justifyContent: 'space-between' }}
              >
                <Box sx={{ minWidth: { md: 240 } }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {activityType || 'Unknown activity'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activities.length} selected item
                    {activities.length === 1 ? '' : 's'}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, maxWidth: { md: 720 } }}>
                  {compatibleSuppliers.length ? (
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={compatibleSuppliers}
                      value={selectedSuppliers}
                      getOptionLabel={supplierLabel}
                      isOptionEqualToValue={(option, value) =>
                        option.supplierId === value.supplierId
                      }
                      onChange={(_, values) => {
                        const selectedIds = new Set(
                          values.map((supplier) => supplier.supplierId),
                        )
                        const groupKeys = new Set(activityKeys)
                        setAllocations((current) => {
                          const next = { ...current }
                          compatibleSuppliers.forEach((supplier) => {
                            const id = supplier.supplierId!
                            const otherKeys = (current[id] ?? []).filter(
                              (key) => !groupKeys.has(key),
                            )
                            const updated = selectedIds.has(id)
                              ? [...new Set([...otherKeys, ...activityKeys])]
                              : otherKeys
                            if (updated.length) next[id] = updated
                            else delete next[id]
                          })
                          return next
                        })
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label={`Suppliers for ${activityType || 'unknown activity'}`}
                          placeholder="Search suppliers..."
                        />
                      )}
                      renderOption={(props, option, state) => {
                        const { key, ...optionProps } = props
                        return (
                          <Box component="li" key={key} {...optionProps}>
                            <Checkbox
                              checked={state.selected}
                              sx={{ mr: 1 }}
                            />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {supplierLabel(option)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.glNumber || 'No GL number'}
                              </Typography>
                            </Box>
                          </Box>
                        )
                      }}
                    />
                  ) : (
                    <Alert severity="warning" sx={{ py: 0 }}>
                      No compatible suppliers
                    </Alert>
                  )}
                </Box>
              </Stack>
            </Paper>
          )
        })}
      </Stack>
    )
  }

  const reviewAllocations = suppliers.filter(
    (supplier) =>
      supplier.supplierId && allocations[supplier.supplierId]?.length,
  )
  const renderReview = () => {
    const project = store.getState().project
    return (
      <Stack spacing={2}>
      <Alert severity="info">
        Review the exact activity scope for each supplier before sending. Email
        delivery includes a secure supplier response link.
      </Alert>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Typography color="text.secondary">
          {buyerReference || 'No buyer reference'} · responses due{' '}
          in {responseDeadlineWorkingDays} working days
          {requestedDeliveryWorkingDays
            ? ` · delivery requested in ${requestedDeliveryWorkingDays} working days`
            : ''}
        </Typography>
      </Paper>
      {reviewAllocations.map((supplier) => {
        const id = supplier.supplierId!
        const keys = allocations[id]
        return (
          <Paper key={id} variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ justifyContent: 'space-between', gap: 1 }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {supplierLabel(supplier)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {keys.length} activit{keys.length === 1 ? 'y' : 'ies'}{' '}
                  included
                </Typography>
              </Box>
              <TextField
                select
                size="small"
                label="Delivery method"
                value={channels[id] ?? QuotationExchangeChannel.Manual}
                onChange={(event) =>
                  setChannels((current) => ({
                    ...current,
                    [id]: event.target.value as QuotationExchangeChannel,
                  }))
                }
                sx={{ minWidth: 220 }}
              >
                {(
                  supplier.configuredEndpoints?.map(
                    (endpoint) => endpoint.channel,
                  ) ?? [QuotationExchangeChannel.Manual]
                )
                  .filter((value, index, all) => all.indexOf(value) === index)
                  .map((channel) => (
                    <MenuItem key={channel} value={channel}>
                      {channel === QuotationExchangeChannel.Email
                        ? 'Email with secure response link'
                        : channel}
                    </MenuItem>
                  ))}
              </TextField>
            </Stack>
            <Divider sx={{ my: 1 }} />
            <TableContainer>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Activity</TableCell>
                    <TableCell align="right">Project qty.</TableCell>
                    <TableCell>Requested quantities</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedActivities
                    .filter(({ key }) => keys.includes(key))
                    .map(({ line, activity, key }) => {
                      const pointer = line.bomItemPointer
                      const item =
                        pointer?.type === BomItemType.assemblyType
                          ? project.assemblyHeaders?.[pointer.id]
                          : pointer?.type === BomItemType.partType
                            ? project.partTypes?.[pointer.id]
                            : undefined
                      const itemName =
                        item?.name ||
                        item?.originalFileName ||
                        'Unnamed BOM item'
                      const itemQuantity =
                        item?.financial?.totalProjectQuantity ??
                        item?.financial?.quantity
                      return (
                        <TableRow key={key}>
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1.25}
                              sx={{ alignItems: 'center' }}
                            >
                              {pointer && (
                                <QuantityBreakItemThumbnail
                                  pointer={pointer}
                                  itemName={itemName}
                                  rowNumber={item?.rowNumber}
                                />
                              )}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {itemName}
                                </Typography>
                                <Tooltip title={pointer?.id ?? ''}>
                                  <Typography variant="caption" color="text.secondary">
                                    {`${item?.rowNumber ? `#${item.rowNumber} · ` : ''}${
                                      item?.drawingNumber
                                        ? `Drawing ${item.drawingNumber}${item.revision ? ` · Rev. ${item.revision}` : ''}`
                                        : pointer?.type === BomItemType.assemblyType
                                          ? 'Assembly'
                                          : 'Part'
                                    }`}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color="primary"
                              variant="outlined"
                              label={activity.workingStepType || 'Unknown activity'}
                            />
                          </TableCell>
                          <TableCell align="right">{itemQuantity ?? '-'}</TableCell>
                          <TableCell>
                            {activity.quantityBreaks?.join(', ') || '-'} pcs
                          </TableCell>
                        </TableRow>
                      )
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )
      })}
      </Stack>
    )
  }

  const content = [
    renderScope,
    renderDetails,
    renderQuantities,
    renderSuppliers,
    renderReview,
  ][activeStep]()
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      slotProps={{ paper: { sx: { height: { xs: '100%', sm: '88vh' } } } }}
    >
      <DialogTitle>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          sx={{ justifyContent: 'space-between', gap: 1 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Draft Request for Quotation
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Activities are the quotation unit. BOM items provide project
              context.
            </Typography>
          </Box>
          <Chip
            color="primary"
            variant="outlined"
            label={`${selectedActivities.length} selected activit${selectedActivities.length === 1 ? 'y' : 'ies'}`}
          />
        </Stack>
      </DialogTitle>
      <Stepper
        nonLinear
        activeStep={activeStep}
        alternativeLabel
        sx={{ px: 3, pb: 2 }}
      >
        {steps.map((label, index) => (
          <Step key={label} completed={index < activeStep}>
            <StepButton onClick={() => goToStep(index)}>
              <StepLabel>{label}</StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <Divider />
      <DialogContent sx={{ py: 2 }}>
        {stepError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setStepError('')}
          >
            {stepError}
          </Alert>
        )}
        {content}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'flex-end' }}>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={
              activeStep
                ? () => {
                    setStepError('')
                    setActiveStep((step) => step - 1)
                  }
                : onClose
            }
            startIcon={activeStep ? <ArrowBackOutlined /> : undefined}
          >
            {activeStep ? 'Back' : 'Cancel'}
          </Button>
          {activeStep >= 2 && (
            <Button
              startIcon={<SaveOutlined />}
              disabled={
                Boolean(loading.CreateRequestForQuotation) || publishing
              }
              onClick={() => save(false)}
            >
              Save draft
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardOutlined />}
              onClick={next}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={
                publishing ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendOutlined />
                )
              }
              disabled={publishing || !reviewAllocations.length}
              onClick={() => save(true)}
            >
              Publish & send
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

export const OpenRFQDraftDialogButton = () => {
  const { openDialog } = useDialogManager()
  return <Button onClick={() => openDialog('DraftRFQDialog')}>Draft RFQ</Button>
}
